import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from "react-router-dom";
import Grid from '@mui/material/Grid2';

import { 
  Campaign,
  Character,
} from "@dndModels";
import { User } from "@models";

import API from "@dndUtilities/system_api";
import SmartAPI from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import EditableLabel from "@components/input/strings/EditableLabel";

import CharacterMainDetails from "@dndComponents/character/CharacterMainDetails";
import LoadingBox from "@components/input/LoadingBox";

import CharacterBackgroundButton from "@dndComponents/character/background/CharacterBackgroundButton";
import CharacterRaceButton from "@dndComponents/character/race/CharacterRaceButton";
import CharacterClassButton from "@dndComponents/character/classes/CharacterClassButton";
// import CharacterHP from "./misc/CharacterHP";
import CharacterRestButton from "@dndComponents/character/misc/CharacterRestButton";
import CharacterInspirationButton from "@dndComponents/character/misc/CharacterInspirationButton";
import CharacterGiftButton from "@dndComponents/character/gifts/CharacterGiftButton";
import CharacterImageButton from "@dndComponents/character/misc/CharacterImageButton";
import CharacterSpeedButton from "@dndComponents/character/misc/CharacterSpeedButton";
import CharacterACButton from "@dndComponents/character/misc/CharacterACButton";
import CharacterTransformButton from "@dndComponents/character/transform/CharacterTransformButton";
import CharacterSizeButton from "@dndComponents/character/misc/CharacterSizeButton";
import CharacterKnowledgeButton from "@dndComponents/character/knowledge/CharacterKnowledgeButton";
import CustomRolls from "@dndComponents/character/CustomRolls";
import CharacterInitiative from "@dndComponents/character/misc/CharacterInitiative";
import HPInput from "@dndComponents/other/HPInput";
import CharacterAdvantageButton from "@dndComponents/character/misc/CharacterAdvantageButton";
import CharacterColorsButton from "@dndComponents/character/colors/CharacterColorsButton";
import CharacterBastionButton from "@dndComponents/character/bastions/CharacterBastionButton";
import CharacterMailButton from "@dndComponents/character/receivables/CharacterMailButton";
import CharacterLevelDisplay from "@dndComponents/character/misc/CharacterLevelDisplay";
import CharacterProficiencyBonus from "@dndComponents/character/misc/CharacterProficiencyBonus";

interface Props { 
  character: Character;
  shared?: boolean;
}

const api = API.getInstance();
const smart_api = SmartAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterSheet({ 
  character,
  shared = false,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading_detail, setLoadingDetail] = useState("");
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  // const [char_interface, setCharInterface] = useState<CharacterSheetInterface>(character.transform === null ? character : character.transform);
  const [owner, setOwner] = useState<User | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculate = useCallback(() => {
    console.log("recalculate");
    setTimeout(() => {
      char_util.repopulateCharFeatureBases(character);
    }, 1000);
  }, [character]);

  const spellsChanged = useCallback( async () => {
    setTimeout( async () => {
      console.log("spellsChanged");
      await char_util.doRecalcs2(character, character);
      char_util.recalcSpells(character);
    }, 1000);
  }, [character]);

  const transformed = useCallback(() => {
    console.log(character.transform);
    
  }, [character]);
  
  const load = useCallback(() => {
    // debugger;
    const new_obj = character;
    if (new_obj.campaign_id === "" || !api.online) {
      // setCharInterface(new_obj.transform === null ? new_obj : new_obj.transform);
      if (api.user && new_obj.owner_id === api.user._id) {
        setOwner(api.user);
      } else {
        setLoading(true);
        const filter = {
          "_id*ObjectIDs": [new_obj.owner_id]
        };
        
        if (smart_api.apis.user) {
          smart_api.apis.user.getObjects({
            data_type: "user", 
            filter,
            ignore_templates: false
          }).then(res => {
            if (res.length === 1 && res[0] instanceof User) {
              setOwner(res[0]);
            }
            setLoading(false);
          });
        }
      }
    } else {
      setLoading(true);
      setLoadingDetail("Campaign");
      // debugger;
      api.getObjects({ data_type: "campaign", filter: { "_id*ObjectIDs": [new_obj.campaign_id] } }).then(res2 => {
        if (res2.length === 1 && res2[0] instanceof Campaign) {
          const new_campaign = res2[0];
          api.getObjects({data_type: "character", filter: { campaign_id: new_campaign._id }}).then(res3 => {
            new_campaign.characters = res3 as Character[];
            new_obj.campaign = new_campaign;
            // setCharInterface(new_obj.transform === null ? new_obj : new_obj.transform);
            setCampaign(new_campaign);
            if (api.user && new_obj.owner_id === api.user._id) {
              setOwner(api.user);
              setLoading(false);
            } else {
              setLoading(false);
              const filter = {
                "_id*ObjectIDs": [new_obj.owner_id]
              };
        
              if (smart_api.apis.user) {
                smart_api.apis.user.getObjects({
                  data_type: "user", 
                  filter,
                  ignore_templates: false
                }).then(res => {
                  if (res.length === 1 && res[0] instanceof User) {
                    setOwner(res[0]);
                  }
                  setLoading(false);
                });
              }
            }
          });
        }
      });
    }
    setFetched(true);
  }, [character]);

  // const subscribed = useRef(false);

  useEffect(() => {
    // if (subscribed.current) return;
    // subscribed.current = true;
  
    character.subscription_holder.subscribe("Recalculate", mounted, recalculate);
    character.subscription_holder.subscribe("Transform", mounted, transformed);
    character.subscription_holder.subscribe("Spells", mounted, spellsChanged);
    setTimeout(() => {
      load();
    }, 100);
  }, [character, load, recalculate, spellsChanged, transformed]);

  const renderBar1 = () => {
    // console.log("renderBar1");
    const recalc_time = character.recalc_time;
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        className="bg_black_transp"
        size={12}>
        <Grid size={4}>
          <div style={{ float: "left" }}>
            <CharacterImageButton 
              character={character}
              disabled={shared}
            />
          </div>
          <div style={{ float: "left" }}>
            <Grid container spacing={0} direction="row">
              <Grid
                style={{ marginLeft: "4px" }}
                size={12}>
                <EditableLabel 
                  name="Name"
                  value={character.name}
                  fontSize={24}
                  disabled={shared}
                  flash_on_empty
                  onChange={(changed: string) => {
                    const new_obj = character;
                    new_obj.name = changed;
                    new_obj.changed();
                  }}
                />
              </Grid>
              <Grid
                style={{ marginLeft: "4px" }}
                size={12}>
                <CharacterLevelDisplay character={character} />
              </Grid>
              { owner && 
                <Grid
                  style={{ 
                      fontSize: "8px", 
                      color: "gray", 
                      marginLeft: "4px"
                    }}
                  size={12}>
                  { owner.profile_name }
                </Grid>
              }
              <Grid
                style={{ marginLeft: "4px" }}
                size={12}>
                <CharacterTransformButton
                  character={character}
                  show_name
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={5}>
          <Grid size={12}>
            <CharacterClassButton 
              character={character}
              disabled={shared}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <CharacterRaceButton
              character={character}
              disabled={shared}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            { !character.sidekick &&
              <CharacterBackgroundButton
                character={character}
                disabled={shared}
              />
            }
          </Grid>
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={3}>
          <Grid size={12}>
            <div style={{ float: "right", marginRight: "4px", maxWidth: "245px" }}>
              <Grid container spacing={0} direction="row">
                <Grid size={12}>
                  <HPInput 
                    char={character.char_interface}
                  />
                </Grid>
                <Grid size={12}>
                  <CharacterInspirationButton 
                    char={character}
                  />
                </Grid>
                <Grid size={12}>
                  <CharacterAdvantageButton 
                    character={character}
                  />
                </Grid>
              </Grid>
            </div>
            <div style={{ float: "right", marginRight: "4px", maxWidth: "125px" }}>
              <Grid container spacing={0} direction="row">
                <Grid size={4}>
                  <CharacterRestButton 
                    character={character}
                  />
                </Grid>
                <Grid size={4}>
                  <CharacterKnowledgeButton 
                    char={character}
                  />
                </Grid>
                <Grid size={4}>
                  <CharacterGiftButton 
                    character={character}
                    disabled={shared}
                    recalc_time={recalc_time}
                  />
                </Grid>
                { character.character_options.use_bastions && 
                  <Grid size={4}>
                    <CharacterBastionButton 
                      character={character}
                      disabled={shared}
                      recalc_time={recalc_time}
                    />
                  </Grid>
                }
                <Grid size={4}>
                  <CharacterTransformButton
                    character={character}
                  />
                </Grid>
                { character.campaign &&
                  <Grid size={4}>
                    <CharacterMailButton 
                      char={character}
                      disabled={shared}
                      recalc_time={recalc_time}
                    />
                  </Grid>
                }
                { !shared &&
                  <Grid size={4}>
                    <CharacterColorsButton 
                      character={character}
                    />
                  </Grid>
                }
              </Grid>
            </div>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  const renderBar2 = () => {
    // console.log("renderBar2");
    return (
      <>
        <Grid
          style={{ borderTop: "1px solid" }}
          className="bg_black_transp"
          size={{
            xs: 3
          }}>
          <CharacterSizeButton
            character={character}
          />
        </Grid>
        <Grid
          style={{ borderTop: "1px solid" }}
          className="bg_black_transp"
          size={{
            xs: 9
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 3
              }}>
              <CharacterProficiencyBonus
                character={character}
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <CharacterSpeedButton
                character={character}
              />
            </Grid>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 3
              }}>
              <CharacterInitiative
                character={character}
              />
            </Grid>
            <Grid size={3}>
              <CharacterACButton
                char={character}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox message={loading_detail} size={window.innerHeight * 0.5} height={window.innerHeight - 81} />);
  } else if (api.user === null && !shared) {
    return <Navigate to="/" />;
  } else { 
    // console.log("Char Sheet");
    return (
      <Grid container spacing={0} direction="row" className="dracoLich" style={{ 
        lineHeight: "1.5", 
        backgroundSize: `${window.innerWidth}px`, 
        backgroundAttachment: "fixed", 
        backgroundImage: (character.bg_image_url === "" ? undefined : `url(${character.bg_image_url})`),
      }}>
        <Grid
          container
          spacing={0}
          direction="row"
          size={12}>
          { renderBar1() }
          { renderBar2() }
        </Grid>
        <Grid size={12}>
          <CharacterMainDetails 
            character={character}
            campaign={campaign}
            shared={shared}
          />
        </Grid>
        <CustomRolls
          char_interface={character.char_interface}
        />
      </Grid>
    ); 
  }
}
